import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { formatDate } from '../../utils/dateUtils';
import vehicleTechnicalService from '../../services/vehicleTechnicalService';

type StatusColor = 'default' | 'primary' | 'error' | 'warning';

interface TechnicalInspectionPanelProps {
  vehicle: Vehicle;
  onUploadDocument: () => void;
}

const TechnicalInspectionPanel: React.FC<TechnicalInspectionPanelProps> = ({
  vehicle,
  onUploadDocument
}) => {
  const { t } = useTranslation();
  const { technicalInspection } = vehicle;
  
  // Initialize default technicalInspection if not present
  if (!technicalInspection) {
    console.warn('Technical inspection data not initialized for vehicle:', vehicle.id);
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {t('vehicle.technicalInspection.error.dataNotInitialized')}
        </Typography>
      </Box>
    );
  }

  // Get the rules for this vehicle type
  const rules = vehicleTechnicalService.getRules('FR', vehicle.vehicleType);
  if (!rules) {
    console.error('No technical inspection rules found for vehicle type:', vehicle.vehicleType);
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {t('vehicle.technicalInspection.error.noRulesFound')}
        </Typography>
      </Box>
    );
  }

  const status = vehicleTechnicalService.calculateInspectionStatus(vehicle);
  const isPollutionCheckRequired = vehicleTechnicalService.isPollutionCheckRequired(
    vehicle,
    rules
  );

  const getStatusColor = (status: string): StatusColor => {
    switch (status) {
      case 'valid':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'expired':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateSafely = (timestamp: any, errorMessage = 'Error formatting date'): string => {
    if (!timestamp) return '-';
    try {
      if (typeof timestamp.toDate === 'function') {
        return formatDate(timestamp.toDate());
      }
      console.warn(errorMessage, 'Invalid timestamp format');
      return '-';
    } catch (error) {
      console.error(errorMessage, error);
      return '-';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {t('vehicle.technicalInspection.title')}
        </Typography>
        <Chip
          label={t(`vehicle.technicalInspection.status.${status}`)}
          color={getStatusColor(status)}
          sx={{ ml: 2 }}
        />
      </Box>

      {/* Règles applicables */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          {t('vehicle.technicalInspection.rules.title')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('vehicle.technicalInspection.rules.firstInspection')}
            </Typography>
            <Typography variant="body1">
              {rules.firstInspectionMonths} {t('vehicle.technicalInspection.rules.months')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('vehicle.technicalInspection.rules.regularInspection')}
            </Typography>
            <Typography variant="body1">
              {rules.regularInspectionMonths} {t('vehicle.technicalInspection.rules.months')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('vehicle.technicalInspection.rules.inspectionWindow')}
            </Typography>
            <Typography variant="body1">
              {rules.inspectionWindowMonths} {t('vehicle.technicalInspection.rules.months')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('vehicle.technicalInspection.rules.counterVisit')}
            </Typography>
            <Typography variant="body1">
              {rules.counterVisitMonths} {t('vehicle.technicalInspection.rules.months')}
            </Typography>
          </Box>
        </Box>
        {rules.pollutionCheck && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {t('vehicle.technicalInspection.rules.pollutionCheck.title')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('vehicle.technicalInspection.rules.pollutionCheck.frequency')}
                </Typography>
                <Typography variant="body1">
                  {rules.pollutionCheck.regularCheckMonths} {t('vehicle.technicalInspection.rules.months')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('vehicle.technicalInspection.rules.pollutionCheck.window')}
                </Typography>
                <Typography variant="body1">
                  {rules.pollutionCheck.checkWindowEnd} {t('vehicle.technicalInspection.rules.months')}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* État actuel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('vehicle.technicalInspection.lastInspection')}
        </Typography>
        <Typography gutterBottom>
          {formatDateSafely(technicalInspection?.lastInspectionDate, 'Error formatting last inspection date')}
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('vehicle.technicalInspection.nextInspection')}
        </Typography>
        <Typography gutterBottom>
          {formatDateSafely(technicalInspection?.nextInspectionDate, 'Error formatting next inspection date')}
        </Typography>

        {isPollutionCheckRequired && (
          <React.Fragment>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('vehicle.technicalInspection.pollutionCheck.title')}
            </Typography>
            <Typography gutterBottom>
              {t('vehicle.technicalInspection.pollutionCheck.nextCheck')}
              {': '}
              {formatDateSafely(technicalInspection?.nextPollutionCheckDate, 'Error formatting pollution check date')}
            </Typography>
          </React.Fragment>
        )}

        {technicalInspection?.counterVisitDueDate && (
          <React.Fragment>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ 
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              p: 2,
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('vehicle.technicalInspection.counterVisit.required')}
              </Typography>
              <Typography>
                {t('vehicle.technicalInspection.counterVisit.deadline')}
                {': '}
                {formatDateSafely(technicalInspection.counterVisitDueDate, 'Error formatting counter visit date')}
              </Typography>
            </Box>
          </React.Fragment>
        )}
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('vehicle.technicalInspection.history.title')}
        </Typography>
        <List>
          {technicalInspection?.history?.map((record, index) => (
            <ListItem
              key={index}
              sx={{
                backgroundColor: 'background.default',
                mb: 1,
                borderRadius: 1
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography>
                      {t(`vehicle.technicalInspection.history.type.${record.type}`)}
                    </Typography>
                    <Chip
                      size="small"
                      label={t(`vehicle.technicalInspection.history.result.${record.result}`)}
                      color={record.result === 'pass' ? 'success' : 'error'}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2">
                      {formatDateSafely(record.date, 'Error formatting history date')}
                    </Typography>
                    <Typography variant="body2">
                      {record.center}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
        {(!technicalInspection?.history || technicalInspection.history.length === 0) && (
          <Typography color="text.secondary">
            {t('vehicle.details.messages.noDocuments')}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onUploadDocument}
        >
          {t('vehicle.details.buttons.uploadDocument')}
        </Button>
      </Box>
    </Box>
  );
};

export default TechnicalInspectionPanel;
